import Icon from '@ant-design/icons';
import { useMemoizedFn } from 'ahooks';
import { message } from 'antd';
import classNames from 'classnames';
import { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import Recorder from 'recorder-core';
import 'recorder-core/src/engine/wav';

import ImportSvg from '@/components/Icon/svgs/Import.svg';
import AudioPauseSvg from '@/components/Icon/svgs/audio_pause.svg';
import AudioPlaySvg from '@/components/Icon/svgs/audio_play.svg';
import RecordingSvg from '@/components/Icon/svgs/mic_fill.svg';
import LottieJumpLoading from '@/components/chat-app/components/LottieJumpLoading';
import { LAYOUT_SIZE } from '@/hooks/useLayoutSize';
import { useModel } from '@/hooks/useModel';
import useStorage from '@/hooks/useStorage';
import { useTranslation } from '@/i18n/client';
import { cloneVoice, confirmCloneVoice } from '@/service/voice';
import { formatDuration } from '@/utils/timestamp-format';

import styles from './index.less';

export interface VoiceSettingsRef {
  stopAllAudio: () => void;
}

function VoiceSettings(
  props: { className?: string; handleConfirmCb?: () => void },
  ref: React.Ref<VoiceSettingsRef>
) {
  const { className, handleConfirmCb } = props;

  // Define a type for step values for better code readability
  type StepType = 0 | 1;
  // 0 record (including uploading & processing)
  // 1 confirm

  const { mindDetail, loadUserDetail } = useModel('auth-model', (model) => ({
    mindDetail: model.mindDetail,
    loadUserDetail: model.loadUserDetail
  }));

  const { t } = useTranslation();

  const titleTNameMap = {
    0: t('voice.cloneVoiceModal.step1.title'),
    1: t('voice.cloneVoiceModal.step2.title')
  };

  const voice = useMemo(() => mindDetail?.voice, [mindDetail]);

  const [layoutSize] = useStorage('layoutSize');
  const inMobile = useMemo(() => layoutSize === LAYOUT_SIZE.SMALL, [layoutSize]);

  const [step, setStep] = useState<StepType>(voice?.data?.custom ? 1 : 0);
  const [isCloning, setIsCloning] = useState(false);
  const [cloningCountdown, setCloningCountdown] = useState(20);
  const [voiceListLoading, setVoiceListLoading] = useState(false);

  const [voiceFile, setVoiceFile] = useState<File | null>(null);
  const [isRecording, setIsRecording] = useState(false);
  const [recordedAudio, setRecordedAudio] = useState<Blob | null>(null);
  const [recordingTime, setRecordingTime] = useState(0);
  const [audioSource, setAudioSource] = useState<'upload' | 'record' | null>(null);

  const [demoAudio, setDemoAudio] = useState<string>(voice?.data?.demoAudioUrl || '');
  const [isDemoPlaying, setIsDemoPlaying] = useState(false);

  const [isOriginPlaying, setIsOriginPlaying] = useState(false);

  const audioRef = useRef<HTMLAudioElement>(null);
  const recordingTimerRef = useRef<NodeJS.Timeout | null>(null);
  const demoAudioRef = useRef<HTMLAudioElement | null>(null);
  const originAudioRef = useRef<HTMLAudioElement | null>(null);

  const recorderRef = useRef<any>(null);

  useEffect(() => {
    const url = voice?.data?.originAudioUrl;

    if (!url) return;

    const audio = new Audio();

    audio.preload = 'metadata';
    audio.src = url;

    const onLoaded = () => {
      const duration = audio.duration;

      if (!isNaN(duration) && duration !== Infinity) {
        setRecordingTime(duration);
      }
    };

    audio.addEventListener('loadedmetadata', onLoaded);

    return () => {
      audio.removeEventListener('loadedmetadata', onLoaded);
      audio.pause();
      audio.src = '';
      audio.load();
    };
  }, [voice?.data?.originAudioUrl]);

  // 克隆倒计时
  useEffect(() => {
    if (!isCloning) return;

    setCloningCountdown(20);
    const timer = setInterval(() => {
      setCloningCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);

          return 0;
        }

        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [isCloning]);

  const handleFileUpload = (file: File) => {
    // 检查文件格式
    const allowedTypes = ['audio/mp3', 'audio/mpeg', 'audio/wav', 'audio/x-m4a'];
    const fileType = file.type;

    if (!allowedTypes.includes(fileType)) {
      message.error(t('voice.cloneVoiceModal.upload.audioFormatError'));

      return false;
    }

    const maxSize = 10;

    // 检查文件大小
    const isLtMaxSize = file.size / 1024 / 1024 < maxSize;

    if (!isLtMaxSize) {
      message.error(t('voice.cloneVoiceModal.upload.audioSizeError', { maxSize }));

      return false;
    }

    // 清除之前的录音数据
    if (recordedAudio) {
      setRecordedAudio(null);
    }

    // 创建临时URL并加载音频以检查时长
    const objectUrl = URL.createObjectURL(file);
    const audio = new Audio(objectUrl);

    audio.addEventListener('loadedmetadata', () => {
      // 在加载完成后释放URL
      URL.revokeObjectURL(objectUrl);

      const duration = audio.duration;

      // 检查时长
      if (duration < 10) {
        message.error(t('voice.cloneVoiceModal.upload.audioDurationError'));
        setVoiceFile(null);

        return;
      }

      if (duration > 300) {
        // 5分钟 = 300秒
        message.error(t('voice.cloneVoiceModal.upload.audioDurationLongError'));
        setVoiceFile(null);

        return;
      }

      setRecordingTime(duration);
      setVoiceFile(file);
      setAudioSource('upload');

      // 直接克隆
      setTimeout(() => {
        handleVoiceClone();
      }, 100);
    });

    audio.addEventListener('error', () => {
      // 在加载失败时释放URL
      URL.revokeObjectURL(objectUrl);
      message.error(t('voice.cloneVoiceModal.upload.audioFormatError'));
      setVoiceFile(null);
    });

    return false; // 阻止自动上传
  };

  const initVoiceClone = () => {
    setStep(0);
    setIsRecording(false);
    setRecordedAudio(null);
    setRecordingTime(0);
    setVoiceFile(null);
    setAudioSource(null);
    setDemoAudio('');
    setIsDemoPlaying(false);
    setIsOriginPlaying(false);
    recordingTimerRef.current = null;
    demoAudioRef.current?.pause();
    demoAudioRef.current = null;
    originAudioRef.current?.pause();
    originAudioRef.current = null;
    recorderRef.current = null;
  };

  const handleConfirm = () => {
    setVoiceListLoading(true);
    confirmCloneVoice().then((res) => {
      if (res.data.code == 0) {
        Promise.all([loadUserDetail()]).then(() => {
          handleConfirmCb?.();
          setVoiceListLoading(false);
        });
        message.success(t('voice.cloneVoiceModal.tips.voiceConfirm'));
      }
    });
  };

  // 开始录音
  const startRecording = async () => {
    try {
      // 创建录音实例
      recorderRef.current = Recorder({
        type: 'wav',
        sampleRate: 16000, // 后端需要的采样率
        bitRate: 16, // 16bit PCM
        audioTrackSet: {
          // iOS 兼容性优化
          noiseSuppression: true,
          echoCancellation: true
        }
      });

      // 打开麦克风
      recorderRef.current.open(
        () => {
          // 成功打开麦克风后开始录音
          recorderRef.current.start();

          // 设置录音状态
          setIsRecording(true);

          // 重置录音时间
          setRecordingTime(0);

          // 开始计时
          recordingTimerRef.current = setInterval(() => {
            setRecordingTime((prev) => prev + 1);
          }, 1000);
        },
        () => {
          message.error(t('voice.cloneVoiceModal.speak.recordingPermissionError'));
        }
      );
    } catch (error) {
      message.error(t('error.oops'));
    }
  };

  // 停止录音
  const stopRecording = () => {
    // 检查录音实例是否存在
    if (!recorderRef.current) {
      setIsRecording(false);

      return;
    }

    // 停止录音
    recorderRef.current.stop(
      (blob: Blob, duration: number) => {
        console.log(
          '[stopRecording] Recording stopped, duration(ms):',
          duration,
          'blob size:',
          blob.size
        );

        // 设置录音数据
        setRecordedAudio(blob);

        // 创建 WAV 格式的文件对象
        const file = new File([blob], `recording-${Date.now()}.wav`, {
          type: 'audio/wav'
        });

        // 设置文件和音频来源
        setVoiceFile(file);
        setAudioSource('record');

        // 重置录音状态
        setIsRecording(false);

        // 清除计时器
        if (recordingTimerRef.current) {
          clearInterval(recordingTimerRef.current);
          recordingTimerRef.current = null;
        }

        // 释放录音实例
        recorderRef.current.close();
        recorderRef.current = null;

        // 直接克隆
        console.log('[stopRecording] Preparing to call handleVoiceClone');
        setTimeout(() => {
          console.log('[stopRecording] Calling handleVoiceClone now');
          handleVoiceClone();
        }, 100);
      },
      () => {
        message.error(t('error.oops'));

        // 重置录音状态
        setIsRecording(false);

        // 清除计时器
        if (recordingTimerRef.current) {
          clearInterval(recordingTimerRef.current);
          recordingTimerRef.current = null;
        }
      }
    );
  };

  // 检查录音时长
  useEffect(() => {
    // 如果录音时间超过300秒(5分钟)，自动停止
    if (recordingTime >= 300 && isRecording) {
      stopRecording();
      message.info(t('voice.cloneVoiceModal.speak.recordingTimeLong'));
    }

    // 如果录音时间少于10秒，提示用户
    if (recordingTime < 10 && !isRecording && recordedAudio) {
      message.warning(t('voice.cloneVoiceModal.speak.recordingTimeShort'));
    }
  }, [recordingTime, isRecording, recordedAudio]);

  // 处理声音克隆
  const stopAllAudio = useMemoizedFn(() => {
    if (demoAudioRef.current) {
      demoAudioRef.current.pause();
      demoAudioRef.current = null;
      setIsDemoPlaying(false);
    }

    if (originAudioRef.current) {
      originAudioRef.current.pause();
      originAudioRef.current = null;
      setIsOriginPlaying(false);
    }
  });

  useImperativeHandle(ref, () => ({
    stopAllAudio
  }));

  const handleVoiceClone = useMemoizedFn(async () => {
    console.log(
      '[handleVoiceClone] Start, voiceFile:',
      voiceFile,
      'audioSource:',
      audioSource,
      'recordingTime:',
      recordingTime
    );

    if (!voiceFile) {
      console.log('[handleVoiceClone] No voiceFile, exit');
      message.warning(t('voice.cloneVoiceModal.tips.audioFirst'));

      return;
    }

    // 对于录音数据，直接使用录音时间
    if (audioSource === 'record' && recordingTime) {
      console.log('[handleVoiceClone] Check recording duration:', recordingTime);

      if (recordingTime < 10) {
        console.log('[handleVoiceClone] Duration < 10s, exit');
        message.warning(t('voice.cloneVoiceModal.upload.audioDurationError'));

        return;
      }

      if (recordingTime > 300) {
        console.log('[handleVoiceClone] Duration > 300s, exit');
        message.warning(t('voice.cloneVoiceModal.upload.audioDurationLongError'));

        return;
      }

      console.log('[handleVoiceClone] Duration check passed');
    }
    // 对于上传的文件，使用音频元素的时长
    else if (audioSource === 'upload' && audioRef.current) {
      // 确保音频已经加载完成
      if (audioRef.current.readyState >= 1) {
        const duration = audioRef.current.duration;

        if (duration === Infinity || isNaN(duration)) {
          // 如果无法获取时长，则跳过检查
        } else {
          if (duration < 10) {
            message.warning(t('voice.cloneVoiceModal.upload.audioDurationError'));

            return;
          }

          if (duration > 300) {
            message.warning(t('voice.cloneVoiceModal.upload.audioDurationLongError'));

            return;
          }
        }
      }
    }

    console.log('[handleVoiceClone] Calling clone API');
    setIsCloning(true);

    cloneVoice({ file: voiceFile })
      .then((res) => {
        console.log('[handleVoiceClone] API response:', res);

        if (res.data.code !== 0) {
          console.log(
            '[handleVoiceClone] Clone failed, code:',
            res.data.code,
            'message:',
            res.data.message
          );

          throw new Error(res.data.message);
        }

        console.log('[handleVoiceClone] Clone success, switching to step 2');
        setStep(1);
        setDemoAudio(res.data.data.demoAudio);
      })
      .catch((error) => {
        console.error('[handleVoiceClone] Error:', error);
        message.error(error.message);
      })
      .finally(() => {
        console.log('[handleVoiceClone] Process finished');
        setIsCloning(false);
      });
  });

  return (
    <div className={classNames(styles.container, { [styles.inMobile]: inMobile }, className)}>
      <div className={styles.headerContainer}>
        {step === 0 && !isCloning ? (
          <label className={styles.uploadLabel}>
            <input
              accept="audio/mp3, audio/mpeg, audio/wav, audio/x-m4a"
              multiple={false}
              onChange={(e) => {
                if (!e.target.files?.[0]) {
                  return;
                }

                handleFileUpload(e.target.files[0]);
              }}
              type="file"
            />
            <Icon className="text-[20px] text-[#B8AD9B]" component={ImportSvg} />
            <div className="text-[14px] leading-6 text-[#231309]">
              {t('voice.cloneVoiceModal.upload.title')}
            </div>
          </label>
        ) : null}
      </div>

      <div className={styles.contentContainer}>
        <div>
          <div className={classNames(styles.title, { [styles.loading]: isCloning })}>
            {isCloning ? t('voice.learningTheWayYouSpeak') : titleTNameMap[step]}
          </div>
          {isCloning && cloningCountdown > 0 && (
            <div className="text-center text-[16px] leading-[28px] font-[400] text-[#5D5446] ">
              {t('voice.estimatedApprox', { seconds: cloningCountdown })}
            </div>
          )}
        </div>

        {step === 0 ? (
          <div className={styles.cloneContainer}>
            <div className={styles.tipsContainer}>
              <img
                className="w-[37px] h-[22px] flex-shrink-0"
                src="https://mindverseglobal.mindverse.com/front-img/crm/1749550519979/voiceCloneMebot.png"
              />
              <div className="text-base leading-7 text-[#5D5446]">
                {t('voice.cloneVoiceModal.step1.desc')}
              </div>
            </div>
            <div className="text-[20px] leading-9 text-primary-new-black mt-2">
              {t('voice.cloneVoiceModal.step1.text')}
            </div>
            {isCloning ? null : (
              <div className={styles.recordingContainer}>
                <div
                  className={classNames(styles.recordingBtn, { [styles.recording]: isRecording })}
                  onClick={() => {
                    if (isRecording) {
                      stopRecording();
                    } else {
                      startRecording();
                    }
                  }}
                >
                  <Icon className={styles.recordingBtnIcon} component={RecordingSvg} />
                </div>
                <div className={styles.tips}>
                  {isRecording
                    ? t('voice.cloneVoiceModal.speak.recording', { time: recordingTime })
                    : t('voice.cloneVoiceModal.speak.tapToSpeak')}
                </div>
              </div>
            )}
          </div>
        ) : null}

        {step === 1 ? (
          <div className={styles.confirmContainer}>
            <div className={styles.voiceContainer}>
              <div
                className="bg-[#231309] w-10 h-10 rounded-[20px] flex items-center justify-center cursor-pointer"
                onClick={() => {
                  // 如果已有正在播放的音频，停止它
                  if (demoAudioRef.current) {
                    demoAudioRef.current.pause();
                    demoAudioRef.current = null;
                    setIsDemoPlaying(false);

                    return;
                  }

                  // 创建新的音频并播放
                  const audio = new Audio(demoAudio);

                  demoAudioRef.current = audio;

                  // 设置播放结束事件
                  audio.addEventListener('ended', () => {
                    setIsDemoPlaying(false);
                    demoAudioRef.current = null;
                  });

                  // 播放音频
                  audio.play();
                  setIsDemoPlaying(true);
                }}
              >
                {isDemoPlaying ? (
                  <Icon className="text-[20px] text-primary-white" component={AudioPauseSvg} />
                ) : (
                  <Icon className="text-[20px] text-primary-white" component={AudioPlaySvg} />
                )}
              </div>
              <div className="text-[16px] leading-7 font-medium text-primary-new-black">
                {t('voice.cloneVoiceModal.step2.listenClonedVoice')}
              </div>
            </div>

            {voiceFile || voice?.data?.originAudioUrl ? (
              <div
                className={styles.originVoice}
                onClick={() => {
                  // 如果已有正在播放的音频，停止它
                  if (originAudioRef.current) {
                    originAudioRef.current.pause();
                    originAudioRef.current = null;
                    setIsOriginPlaying(false);

                    return;
                  }

                  // 创建新的音频并播放
                  const objectUrl = voiceFile
                    ? URL.createObjectURL(voiceFile)
                    : voice?.data?.originAudioUrl;
                  const audio = new Audio(objectUrl);

                  originAudioRef.current = audio;

                  // 设置播放结束事件
                  audio.addEventListener('ended', () => {
                    setIsOriginPlaying(false);
                    originAudioRef.current = null;
                  });

                  // 播放音频
                  audio.play();
                  setIsOriginPlaying(true);
                }}
              >
                {isOriginPlaying ? (
                  <Icon className="text-[20px] text-[#756C5D]" component={AudioPauseSvg} />
                ) : (
                  <Icon className="text-[20px] text-[#756C5D]" component={AudioPlaySvg} />
                )}
                <div>{t('voice.cloneVoiceModal.step2.originalAudio')}</div>
                <div>{formatDuration(recordingTime * 1000)}</div>
              </div>
            ) : null}

            <div className="flex w-full flex-col gap-1">
              <div className="text-base font-medium leading-7 text-primary-new-black">
                {t('voice.cloneVoiceModal.step2.desc')}
              </div>
              <div className="flex w-full flex-col text-sm leading-6 text-[#5d5446]">
                <li> {t('voice.cloneVoiceModal.step2.li1')}</li>
                <li> {t('voice.cloneVoiceModal.step2.li2')}</li>
                <li> {t('voice.cloneVoiceModal.step2.li3')}</li>
                <li>{t('voice.cloneVoiceModal.step2.li4')}</li>
              </div>
            </div>

            {voiceListLoading ? (
              <LottieJumpLoading className="!w-16 !h-16 mt-12" />
            ) : (
              <>
                <div
                  className="mt-4 w-full flex items-center justify-center px-4 py-2 text-base leading-7 text-primary-white rounded-[22px] cursor-pointer transition-colors bg-primary hover:bg-primary-sub-color"
                  onClick={handleConfirm}
                >
                  {t('voice.cloneVoiceModal.step2.confirm')}
                </div>
                <div
                  className="w-full flex items-center justify-center px-4 py-2 text-base leading-7 text-primary-new-black rounded-[22px] cursor-pointer transition-colors bg-[rgba(254,253,255,0.5)] hover:bg-[rgba(254,253,255,0.8)]"
                  onClick={initVoiceClone}
                >
                  {t('voice.cloneVoiceModal.step2.tryAgain')}
                </div>
              </>
            )}
          </div>
        ) : null}
      </div>
    </div>
  );
}

export default forwardRef(VoiceSettings);
